import { Analytics } from '@util/Analytics';
import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import { isEmail } from 'ts-utils';
import { ApiClient } from '../../api/ApiClient';
import { Color } from '../../config/Color';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { dispatchUserData } from '../../store/userData/UserDataActions';
import { ScreenWrapper } from '../styles/Containers';
import { NextButton, SecondaryButton } from '../styles/LayoutButtons';
import { Testimonial, TestimonialProps } from '../styles/Testimonial';
import { BodySM } from '../styles/Typography';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout, PresentationLayoutConfig } from './layouts/PresentationLayout';

type ReferralProps = {
  testimonial?: TestimonialProps;
  secondaryButtonTitle?: string;
};

export type ReferralScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.Referral,
  PresentationLayoutConfig & ReferralProps,
  ScreenNames
>;

type ReferralScreenProps<ScreenNames extends string> = NavigationProps<
  ReferralScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ReferralScreen = <ScreenNames extends string>(
  props: ReferralScreenProps<ScreenNames>
): ReactElement => {
  const {
    navigation,
    layout: { content, testimonial, ...restLayout },
  } = props;
  const [email, setEmail] = useState('');
  const hasEmail = email.trim().length > 1;

  async function sendReferralEmail(referralEmail: string) {
    /** Sends a referral email to the provided email address.
     * The referral email will include the referrer's name in the title.
     */
    await new ApiClient().sendFriendReferral({ referralEmail });
  }

  function onSubmitPress() {
    if (isEmail(email)) {
      sendReferralEmail(email);
      dispatchUserData({ [props.screenName]: 'Yes' });
      Analytics.track({
        event: Analytics.Event.o_app_referral_action,
        action: 'referralSent',
      });
      navigation.next(props, { resetHistory: true });
    } else {
      alert('Please enter a valid email address.');
    }
  }

  function onSkipPress() {
    dispatchUserData({ [props.screenName]: 'No' });
    Analytics.track({
      event: Analytics.Event.o_app_referral_action,
      action: 'referralSkipped',
    });
    navigation.next(props, { resetHistory: true });
  }

  return (
    <ScreenWrapper>
      <PresentationLayout
        content={[
          ...content,
          {
            type: 'custom',
            content: testimonial !== undefined ? <Testimonial {...testimonial} stars={5} /> : null,
          },
          {
            type: 'custom',
            content: (
              <>
                <Heading>Invite a friend and start Finito together</Heading>
                <ReferralEmailInput
                  type="email"
                  placeholder="Enter email address"
                  onChange={(e): void => setEmail(e.target.value)}
                />
                <PrimaryButton disabled={!hasEmail} onClick={onSubmitPress}>
                  {props.layout.primaryButtonTitle ?? 'Got it!'}
                </PrimaryButton>
                <SkipButtonContainer>
                  <SecondaryButton onClick={onSkipPress}>
                    {props.layout.secondaryButtonTitle ?? 'Skip'}
                  </SecondaryButton>
                </SkipButtonContainer>
              </>
            ),
          },
        ]}
        {...restLayout}
      />
    </ScreenWrapper>
  );
};

const Heading = styled(BodySM)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing._20px};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const PrimaryButton = styled(NextButton)`
  margin-top: ${({ theme }) => theme.spacing._16px};
`;

const SkipButtonContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing._24px};
  button {
    display: flex;
    align-items: center;
    align-content: center;
    margin: ${({ theme }) => `${theme.spacing._26px} auto 0`};

    p {
      color: ${Color.content.muted} !important;
    }
  }
`;

const ReferralEmailInput = styled.input`
  border-radius: 3px;
  border: solid 1px ${Color.decorative.accent};
  width: 100%;
  padding: ${({ theme }) => theme.spacing._12px};

  ::placeholder,
  ::-ms-input-placeholder {
    color: #c4c4c4;
  }

  :focus {
    outline: none;
  }
`;
